// src/components/ScenarioTabs/ScenarioTabs.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import Scenario from "../Scenario/Scenario";
import apiService from "../../api";
import { calculateDansMaPoche, calculateDansMaPochePeriod } from "../../utils/scenarioCalculations";
import { AVAILABLE_YEARS } from "../../utils/incomeConstants";
import ConfirmModal from "../Common/ConfirmModal";
import { useConfirm } from "../../hooks/useConfirm";
import { useAuth } from "../../auth/AuthContext";
import UserManagement from "../../auth/UserManagement";
import "./ScenarioTabs.css";

const ScenarioTabs = ({
  scenarios,
  activeScenarioId,
  setActiveScenarioId,
  addScenario,
  removeScenario,
  updateScenario,
  addLane,
  onSave,
  isSaving,
  saveSuccess,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [dansMaPoche, setDansMaPoche] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Scroll state
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Scenario selector dropdown state
  const [showScenarioSelector, setShowScenarioSelector] = useState(false);
  const [selectorSearch, setSelectorSearch] = useState("");
  const selectorRef = useRef(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  // Auth
  const { user, logout } = useAuth();
  const [showUserMgmt, setShowUserMgmt] = useState(false);

  // Get active scenario for save button state
  const activeScenario = scenarios.find(s => s.id === activeScenarioId);
  const hasUnsavedChanges = activeScenario?._hasUnsavedChanges;

  // Check scroll position
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Update scroll state on mount and resize
  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState, scenarios.length]);

  // Scroll handlers
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Calculer DansMaPoche quand le scénario actif change
  const calculateForScenario = useCallback(async (scenario) => {
    if (!scenario) {
      setDansMaPoche(null);
      return;
    }

    // Dates are mandatory - check first before any calculation
    if (!scenario.startDate || !scenario.endDate) {
      setDansMaPoche({
        total: null,
        startingCapital: scenario.startingCapital || 0,
        missingDates: true,
        message: "Veuillez définir les dates de début et de fin du scénario"
      });
      return;
    }

    // No individuals yet - show 0 with starting capital
    if (!scenario.individuals?.length) {
      setDansMaPoche({
        total: scenario.startingCapital || 0,
        startingCapital: scenario.startingCapital || 0,
        details: null
      });
      return;
    }

    setIsCalculating(true);
    try {
      const allIncomes = {};
      for (const ind of scenario.individuals) {
        const id = ind.individual_id || ind.id;
        if (!id) continue;
        try {
          const incomes = await apiService.fetchIncomes(id);
          allIncomes[id] = incomes;
        } catch (e) {
          allIncomes[id] = [];
        }
      }

      const entitiesFinancials = {};
      for (const ent of scenario.entities || []) {
        const id = ent.entity_id || ent.id;
        if (!id) continue;
        try {
          const financials = await apiService.fetchEntityFinancials(id);
          entitiesFinancials[id] = financials;
        } catch (e) {
          entitiesFinancials[id] = [];
        }
      }

      if (scenario.startDate && scenario.endDate) {
        const result = calculateDansMaPochePeriod(scenario, allIncomes, entitiesFinancials);
        setDansMaPoche(result);
      } else {
        // Dates are mandatory for calculation
        setDansMaPoche({
          total: null,
          startingCapital: scenario.startingCapital || 0,
          missingDates: true,
          message: "Veuillez définir les dates de début et de fin du scénario"
        });
      }
    } catch (error) {
      console.error('Error calculating DansMaPoche:', error);
      setDansMaPoche(null);
    } finally {
      setIsCalculating(false);
    }
  }, []);

  useEffect(() => {
    const activeScenario = scenarios.find(s => s.id === activeScenarioId);
    calculateForScenario(activeScenario);
  }, [activeScenarioId, scenarios, calculateForScenario]);

  const sortedScenarios = [...scenarios].sort((a, b) => a.id - b.id);

  // Get current scenario index for counter display
  const currentIndex = sortedScenarios.findIndex(s => s.id === activeScenarioId) + 1;
  const totalScenarios = scenarios.length;

  // Find the most recently created scenario (highest ID = newest)
  const latestScenario = scenarios.length > 0
    ? scenarios.reduce((latest, s) => s.id > latest.id ? s : latest, scenarios[0])
    : null;

  // Filter scenarios for dropdown search
  const filteredScenarios = sortedScenarios.filter(s =>
    s.name.toLowerCase().includes(selectorSearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) {
        setShowScenarioSelector(false);
        setSelectorSearch("");
      }
    };
    if (showScenarioSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showScenarioSelector]);

  // Jump to latest scenario
  const jumpToLatest = () => {
    if (latestScenario) {
      setActiveScenarioId(latestScenario.id);
    }
  };

  // Select scenario from dropdown
  const selectScenario = (scenarioId) => {
    setActiveScenarioId(scenarioId);
    setShowScenarioSelector(false);
    setSelectorSearch("");
  };

  // Navigate to next/previous scenario
  const goToNextScenario = useCallback(() => {
    const currentIdx = sortedScenarios.findIndex(s => s.id === activeScenarioId);
    if (currentIdx < sortedScenarios.length - 1) {
      setActiveScenarioId(sortedScenarios[currentIdx + 1].id);
    }
  }, [activeScenarioId, sortedScenarios, setActiveScenarioId]);

  const goToPreviousScenario = useCallback(() => {
    const currentIdx = sortedScenarios.findIndex(s => s.id === activeScenarioId);
    if (currentIdx > 0) {
      setActiveScenarioId(sortedScenarios[currentIdx - 1].id);
    }
  }, [activeScenarioId, sortedScenarios, setActiveScenarioId]);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Ctrl+] or Ctrl+ArrowRight: Next scenario
      if (e.ctrlKey && (e.key === ']' || e.key === 'ArrowRight')) {
        e.preventDefault();
        goToNextScenario();
      }
      // Ctrl+[ or Ctrl+ArrowLeft: Previous scenario
      else if (e.ctrlKey && (e.key === '[' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        goToPreviousScenario();
      }
      // Ctrl+Shift+] or Ctrl+End: Jump to latest
      else if ((e.ctrlKey && e.shiftKey && e.key === ']') || (e.ctrlKey && e.key === 'End')) {
        e.preventDefault();
        if (latestScenario) {
          setActiveScenarioId(latestScenario.id);
        }
      }
      // Ctrl+G: Open scenario selector
      else if (e.ctrlKey && e.key === 'g') {
        e.preventDefault();
        setShowScenarioSelector(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToNextScenario, goToPreviousScenario, latestScenario, setActiveScenarioId]);

  const handleDoubleClick = (scenario) => {
    setEditingId(scenario.id);
    setEditName(scenario.name);
  };

  const handleNameChange = (e) => {
    setEditName(e.target.value);
  };

  const handleNameBlur = (scenarioId) => {
    if (editName.trim()) {
      updateScenario(scenarioId, (prev) => ({ ...prev, name: editName.trim() }));
    }
    setEditingId(null);
  };

  const handleKeyDown = (e, scenarioId) => {
    if (e.key === 'Enter') {
      handleNameBlur(scenarioId);
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  // Scroll active tab into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const activeTab = container.querySelector('.tab.active');
    if (activeTab) {
      activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeScenarioId]);

  return (
    <div className="scenario-tabs">
      <div className="tabs-bar">
        {/* Scenario navigation controls */}
        <div className="scenario-nav-controls">
          {/* Scenario counter */}
          <span className="scenario-counter" title="Scénario actuel / Total">
            {currentIndex}/{totalScenarios}
          </span>

          {/* Scenario selector dropdown */}
          <div className="scenario-selector" ref={selectorRef}>
            <button
              className="scenario-selector-btn"
              onClick={() => setShowScenarioSelector(!showScenarioSelector)}
              title="Sélectionner un scénario"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </button>

            {showScenarioSelector && (
              <div className="scenario-selector-dropdown">
                <input
                  type="text"
                  className="scenario-selector-search"
                  placeholder="Rechercher..."
                  value={selectorSearch}
                  onChange={(e) => setSelectorSearch(e.target.value)}
                  autoFocus
                />
                <div className="scenario-selector-list">
                  {filteredScenarios.map((scenario, index) => (
                    <div
                      key={scenario.id}
                      className={`scenario-selector-item ${activeScenarioId === scenario.id ? 'active' : ''} ${scenario.id === latestScenario?.id ? 'latest' : ''}`}
                      onClick={() => selectScenario(scenario.id)}
                    >
                      <span className="item-index">{sortedScenarios.indexOf(scenario) + 1}</span>
                      <span className="item-name">{scenario.name}</span>
                      {scenario.id === latestScenario?.id && (
                        <span className="item-badge">Dernier</span>
                      )}
                    </div>
                  ))}
                  {filteredScenarios.length === 0 && (
                    <div className="scenario-selector-empty">Aucun scénario trouvé</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Jump to latest button */}
          {totalScenarios > 1 && latestScenario && activeScenarioId !== latestScenario.id && (
            <button
              className="jump-to-latest-btn"
              onClick={jumpToLatest}
              title={`Aller au dernier: ${latestScenario.name}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 17 18 12 13 7"></polyline>
                <polyline points="6 17 11 12 6 7"></polyline>
              </svg>
            </button>
          )}
        </div>

        {/* Left scroll arrow */}
        <button
          className={`scroll-arrow scroll-left ${canScrollLeft ? 'visible' : ''}`}
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        {/* Scrollable tabs container */}
        <div
          className={`tabs-scroll-container ${canScrollLeft ? 'has-left' : ''} ${canScrollRight ? 'has-right' : ''}`}
        >
          <div
            className="tabs-scroll"
            ref={scrollContainerRef}
            onScroll={updateScrollState}
          >
            {sortedScenarios.map((scenario, index) => (
              <div
                key={scenario.id}
                className={`tab ${activeScenarioId === scenario.id ? "active" : ""}`}
                onClick={() => setActiveScenarioId(scenario.id)}
              >
                <span className="tab-index">{index + 1}</span>
                {editingId === scenario.id ? (
                  <input
                    type="text"
                    className="tab-name-input"
                    value={editName}
                    onChange={handleNameChange}
                    onBlur={() => handleNameBlur(scenario.id)}
                    onKeyDown={(e) => handleKeyDown(e, scenario.id)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <span
                    className="tab-label"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      handleDoubleClick(scenario);
                    }}
                    title={scenario.name}
                  >
                    {scenario.name}
                  </span>
                )}
                <button
                  className="tab-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    showConfirm({
                      title: 'Supprimer le scénario ?',
                      message: `Voulez-vous vraiment supprimer "${scenario.name}" ?`,
                      details: ['Cette action est irréversible.'],
                      confirmText: 'Supprimer',
                      type: 'danger',
                      onConfirm: () => removeScenario(scenario.id)
                    });
                  }}
                  aria-label="Close tab"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right scroll arrow */}
        <button
          className={`scroll-arrow scroll-right ${canScrollRight ? 'visible' : ''}`}
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        {/* Actions container - keeps buttons together */}
        <div className="tabs-actions">
          {/* Add new scenario button */}
          <button
            className="action-btn new-btn"
            onClick={addScenario}
            title="Nouveau scénario"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Nouveau</span>
          </button>

          {/* Save button */}
          <button
            className={`action-btn save-btn ${isSaving ? 'saving' : ''} ${saveSuccess ? 'success' : ''} ${hasUnsavedChanges ? 'has-changes' : ''}`}
            onClick={onSave}
            disabled={isSaving}
            title={hasUnsavedChanges ? 'Modifications non enregistrées' : 'Enregistrer'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <span>{hasUnsavedChanges ? '● ' : ''}Enregistrer</span>
          </button>

          {/* User menu */}
          <div className="user-menu">
            <span className="user-menu-name">{user?.name || user?.email}</span>
            {user?.role === 'admin' && (
              <button
                className="action-btn users-btn"
                onClick={() => setShowUserMgmt(true)}
                title="Gestion utilisateurs"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </button>
            )}
            <button
              className="action-btn logout-btn"
              onClick={logout}
              title="Se deconnecter"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* User Management Modal */}
      <UserManagement isOpen={showUserMgmt} onClose={() => setShowUserMgmt(false)} />

      <div className="scenario-content">
        {activeScenarioId && (
          <Scenario
            scenario={scenarios.find((s) => s.id === activeScenarioId)}
            updateScenario={updateScenario}
            addLane={addLane}
            dansMaPoche={dansMaPoche}
            isCalculating={isCalculating}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default ScenarioTabs;
